import type { BrowserShortcut } from '@crowdex/host-kit/shared/browser-shortcuts';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { electronInvoke, listen } from '@/lib/electron-bridge';
import { errorMessage } from '@/lib/error-message';
import {
  type BrowserAgentCursorController,
  createBrowserAgentCursor,
} from '@/lib/side-panel/browser-agent-cursor';
import type { BrowserSessionMode } from '@/lib/side-panel/browser-session';

type NativeBrowserViewProps = {
  /** The tab is on screen (alone, or in a cell of the split). */
  active: boolean;
  /**
   * The tab is the browser's current one, which commands without a tab id act on: on screen and
   * the tab last shown (one of the split's tabs at most). Defaults to `active`.
   */
  selected?: boolean;
  /**
   * An agent turn drives the tab: while not the visible tab, it stays laid out (its parent keeps
   * it under the visible one, transparent) so that it keeps painting and can be captured.
   */
  driven?: boolean;
  id: string;
  mode?: BrowserSessionMode;
  onSelection: (selection: BrowserSelection) => void;
  onSelectionModeError: (error: string | null) => void;
  onUrlChange: (url: string) => void;
  reloadKey: number;
  selectionEnabled: boolean;
  url: string;
  workspaceRoot: string;
  profileId?: string;
  /** A named isolated profile: the same one for every workspace, kept for good. */
  profile?: string | undefined;
  /** Filled with the page's controls once the view exists. */
  pageRef?: RefObject<BrowserPageControls | null>;
  onHistoryChange?: (history: BrowserHistoryState) => void;
  /** The page started or stopped loading. */
  onLoadingChange?: (loading: boolean) => void;
  /** The matches of the last search in the page ({@link BrowserPageControls.find}). */
  onFindResult?: (result: BrowserFindResult) => void;
  /** A browser shortcut pressed while the page has the focus. */
  onShortcut?: (shortcut: BrowserShortcut) => void;
  /** The page took the keyboard. */
  onFocus?: () => void;
  onFaviconChange?: (favicon: string | null) => void;
  /** The page title, or `null` when a new document starts loading without one yet. */
  onTitleChange?: (title: string | null) => void;
};

export type BrowserHistoryState = { canGoBack: boolean; canGoForward: boolean };
export type BrowserFindResult = { active: number; matches: number };
/** The page's controls a browser toolbar uses. */
export type BrowserPageControls = {
  goBack: () => void;
  goForward: () => void;
  stop: () => void;
  focus: () => void;
  /** Searches the page for `text`, from the current match on (`next`) or anew. */
  find: (text: string, options: { forward: boolean; next: boolean }) => void;
  stopFind: () => void;
  /** The page's zoom, 1 being 100%: Chromium keeps it per site, for every tab of the site. */
  zoomFactor: () => number;
  setZoomFactor: (factor: number) => void;
  /** Reloads bypassing the cache (Chrome's hard reload). */
  reloadIgnoringCache: () => void;
};

export type BrowserSelection = {
  annotation?: string;
  annotationIndex?: number;
  html: string;
  rect: { height: number; width: number; x: number; y: number };
  selector: string;
  screenshotDataUrl?: string;
  tagName: string;
  text: string;
  title: string;
  url: string;
  viewport: { height: number; width: number };
};

type BrowserSelectionEvent = { selection: BrowserSelection; webviewLabel: string };
type BrowserLocationEvent = { url: string; webviewLabel: string };
type BrowserNavigationBlockedEvent = { error: string; url: string; webviewLabel: string };
type BrowserNavigationFailedEvent = { error: string; url: string; webviewLabel: string };
type BrowserAgentCursorEvent = {
  /** `false` when the tab is not on screen: the cursor jumps there (Codex's `animateMovement`). */
  animateMovement?: boolean;
  id: string;
  sequence: number;
  webviewLabel: string;
  x: number;
  y: number;
};
type BrowserAgentActivityEvent = { active: boolean; id: string | null; webviewLabel: string };
type BrowserShortcutEvent = { shortcut: BrowserShortcut; webviewLabel: string };
type PreparedBrowserView = { label: string; partition: string };
type BrowserWebviewElement = HTMLElement & {
  canGoBack: () => boolean;
  canGoForward: () => boolean;
  getWebContentsId: () => number;
  goBack: () => void;
  goForward: () => void;
  stop: () => void;
  reloadIgnoringCache: () => void;
  findInPage: (text: string, options: { forward: boolean; findNext: boolean }) => number;
  stopFindInPage: (action: 'clearSelection') => void;
  getZoomFactor: () => number;
  setZoomFactor: (factor: number) => void;
};

function label(id: string): string {
  return `crowdex-browser-${id.replace(/[^a-zA-Z0-9-]/g, '-')}`;
}

export function NativeBrowserView({
  active,
  selected,
  driven = false,
  id,
  mode = 'shared',
  onSelection,
  onSelectionModeError,
  onUrlChange,
  reloadKey,
  selectionEnabled,
  url,
  workspaceRoot,
  profileId,
  profile,
  pageRef,
  onHistoryChange,
  onLoadingChange,
  onFindResult,
  onShortcut,
  onFocus,
  onFaviconChange,
  onTitleChange,
}: NativeBrowserViewProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(active);
  const selectedRef = useRef(selected ?? active);
  const drivenRef = useRef(driven);
  const createdRef = useRef(false);
  const currentUrlRef = useRef(url);
  const instanceIdRef = useRef('');
  const onSelectionRef = useRef(onSelection);
  const onSelectionModeErrorRef = useRef(onSelectionModeError);
  const onUrlChangeRef = useRef(onUrlChange);
  const onHistoryChangeRef = useRef(onHistoryChange);
  const onFaviconChangeRef = useRef(onFaviconChange);
  const onTitleChangeRef = useRef(onTitleChange);
  const onLoadingChangeRef = useRef(onLoadingChange);
  const onFindResultRef = useRef(onFindResult);
  const onShortcutRef = useRef(onShortcut);
  const onFocusRef = useRef(onFocus);
  const syncBoundsRef = useRef<(() => void) | null>(null);
  const selectionEnabledRef = useRef(selectionEnabled);
  const webviewRef = useRef<BrowserWebviewElement | null>(null);
  const cursorRef = useRef<BrowserAgentCursorController | null>(null);
  const [error, setError] = useState<string | null>(null);
  activeRef.current = active;
  selectedRef.current = selected ?? active;
  drivenRef.current = driven;
  currentUrlRef.current = url;
  onSelectionRef.current = onSelection;
  onSelectionModeErrorRef.current = onSelectionModeError;
  onUrlChangeRef.current = onUrlChange;
  onHistoryChangeRef.current = onHistoryChange;
  onFaviconChangeRef.current = onFaviconChange;
  onTitleChangeRef.current = onTitleChange;
  onLoadingChangeRef.current = onLoadingChange;
  onFindResultRef.current = onFindResult;
  onShortcutRef.current = onShortcut;
  onFocusRef.current = onFocus;
  selectionEnabledRef.current = selectionEnabled;

  useEffect(() => {
    const expected = label(id);
    let stopped = false;
    let cleanups: Array<() => void> = [];
    void Promise.all([
      listen<BrowserSelectionEvent>('browser-selection', ({ payload }) => {
        if (payload.webviewLabel === expected) onSelectionRef.current(payload.selection);
      }),
      listen<BrowserLocationEvent>('browser-location-changed', ({ payload }) => {
        if (payload.webviewLabel === expected) {
          setError(null);
          onUrlChangeRef.current(payload.url);
          const webview = webviewRef.current;
          if (createdRef.current && webview !== null)
            onHistoryChangeRef.current?.({
              canGoBack: webview.canGoBack(),
              canGoForward: webview.canGoForward(),
            });
        }
      }),
      listen<BrowserNavigationBlockedEvent>('browser-navigation-blocked', ({ payload }) => {
        if (payload.webviewLabel === expected) setError(payload.error);
      }),
      listen<BrowserNavigationFailedEvent>('browser-navigation-failed', ({ payload }) => {
        if (payload.webviewLabel === expected) setError(payload.error);
      }),
      listen<BrowserAgentCursorEvent>('browser-agent-cursor-state', ({ payload }) => {
        if (payload.webviewLabel === expected) cursorRef.current?.move(payload);
      }),
      listen<BrowserAgentActivityEvent>('browser-agent-activity-changed', ({ payload }) => {
        if (payload.webviewLabel === expected) cursorRef.current?.setActive(payload.active);
      }),
      listen<BrowserShortcutEvent>('browser-shortcut', ({ payload }) => {
        if (payload.webviewLabel === expected) onShortcutRef.current?.(payload.shortcut);
      }),
    ]).then((next) => {
      if (stopped) for (const cleanup of next) cleanup();
      else cleanups = next;
    });
    return () => {
      stopped = true;
      for (const cleanup of cleanups) cleanup();
    };
  }, [id]);

  useEffect(() => {
    let disposed = false;
    let observer: ResizeObserver | null = null;
    let sync: (() => void) | null = null;
    let animationFrame = 0;
    const host = hostRef.current;
    if (!host) return;
    const instanceId = crypto.randomUUID();
    instanceIdRef.current = instanceId;
    createdRef.current = false;
    setError(null);
    cursorRef.current = createBrowserAgentCursor(host, (sequence) => {
      void electronInvoke('browser_cursor_arrived', { id, instanceId, sequence }).catch(
        () => undefined,
      );
    });
    // The tab may already be driven when it mounts: the activity event came before.
    cursorRef.current.setActive(drivenRef.current);
    void electronInvoke<PreparedBrowserView>('browser_create', {
      id,
      instanceId,
      sessionMode: mode,
      ...(profileId ? { profileId } : {}),
      ...(profile ? { profile } : {}),
      workspaceRoot,
    })
      .then((prepared) => {
        if (disposed) return;
        const webview = document.createElement('webview') as BrowserWebviewElement;
        webviewRef.current = webview;
        webview.setAttribute('partition', prepared.partition);
        webview.setAttribute('src', currentUrlRef.current);
        webview.style.display = 'flex';
        webview.style.height = '100%';
        webview.style.width = '100%';
        const syncView = () => {
          const shown = activeRef.current;
          const current = shown && selectedRef.current;
          webview.style.visibility = shown || drivenRef.current ? 'visible' : 'hidden';
          if (!createdRef.current) return;
          const bounds = host.getBoundingClientRect();
          const laidOut = bounds.width >= 1 && bounds.height >= 1;
          if (!current || !laidOut)
            void electronInvoke('browser_set_active', {
              active: false,
              id,
              instanceId,
            }).catch(() => undefined);
          // A driven background tab still reports its size: the agent's viewport is what it paints.
          if (!laidOut || (!shown && !drivenRef.current)) return;
          void electronInvoke('browser_bounds', {
            id,
            instanceId,
            x: bounds.left,
            y: bounds.top,
            width: bounds.width,
            height: bounds.height,
          })
            .then(() =>
              current
                ? electronInvoke('browser_set_active', { active: true, id, instanceId })
                : null,
            )
            .catch(() => undefined);
        };
        sync = syncView;
        syncBoundsRef.current = syncView;
        observer = new ResizeObserver(syncView);
        observer.observe(host);
        window.addEventListener('resize', syncView);
        window.addEventListener('scroll', syncView, true);
        window.visualViewport?.addEventListener('resize', syncView);
        window.visualViewport?.addEventListener('scroll', syncView);
        const registerWebview = () => {
          void electronInvoke('browser_register', {
            id,
            instanceId,
            webContentsId: webview.getWebContentsId(),
          })
            .then(() => {
              if (disposed) return;
              createdRef.current = true;
              if (pageRef)
                pageRef.current = {
                  goBack: () => webview.goBack(),
                  goForward: () => webview.goForward(),
                  stop: () => webview.stop(),
                  focus: () => webview.focus(),
                  find: (text, { forward, next }) => {
                    webview.findInPage(text, { forward, findNext: !next });
                  },
                  stopFind: () => webview.stopFindInPage('clearSelection'),
                  zoomFactor: () => webview.getZoomFactor(),
                  setZoomFactor: (factor) => webview.setZoomFactor(factor),
                  reloadIgnoringCache: () => webview.reloadIgnoringCache(),
                };
              syncView();
              // A page loaded from a blank tab takes the keyboard, unless something else has it.
              if (activeRef.current && document.activeElement === document.body) webview.focus();
              if (!selectionEnabledRef.current) return;
              return electronInvoke('browser_set_selection_mode', {
                enabled: true,
                id,
                instanceId,
              });
            })
            .catch((reason: unknown) => {
              if (!disposed) setError(errorMessage(reason));
            });
        };
        webview.addEventListener('dom-ready', registerWebview, { once: true });
        webview.addEventListener('page-title-updated', (event) => {
          const title = (event as Event & { title: string }).title.trim();
          if (title.length > 0) onTitleChangeRef.current?.(title);
        });
        webview.addEventListener('page-favicon-updated', (event) => {
          const [favicon] = (event as Event & { favicons: string[] }).favicons;
          onFaviconChangeRef.current?.(
            favicon !== undefined && /^(https?|data):/i.test(favicon) ? favicon : null,
          );
        });
        webview.addEventListener('focus', () => onFocusRef.current?.());
        webview.addEventListener('did-start-loading', () => onLoadingChangeRef.current?.(true));
        webview.addEventListener('did-stop-loading', () => onLoadingChangeRef.current?.(false));
        webview.addEventListener('found-in-page', (event) => {
          const { result } = event as Event & {
            result: { activeMatchOrdinal?: number; matches?: number; finalUpdate: boolean };
          };
          if (result.matches === undefined) return;
          onFindResultRef.current?.({
            active: result.activeMatchOrdinal ?? 0,
            matches: result.matches,
          });
        });
        webview.addEventListener('did-start-navigation', (event) => {
          const { isMainFrame, isSameDocument } = event as Event & {
            isMainFrame: boolean;
            isSameDocument: boolean;
          };
          // A new document starts without the previous page's icon and title.
          if (!isMainFrame || isSameDocument) return;
          onFaviconChangeRef.current?.(null);
          onTitleChangeRef.current?.(null);
        });
        host.appendChild(webview);
        syncView();
        animationFrame = window.requestAnimationFrame(syncView);
        if (disposed) {
          observer.disconnect();
          webview.removeEventListener('dom-ready', registerWebview);
          webview.remove();
          window.removeEventListener('resize', syncView);
          window.removeEventListener('scroll', syncView, true);
          window.visualViewport?.removeEventListener('resize', syncView);
          window.visualViewport?.removeEventListener('scroll', syncView);
        }
      })
      .catch((reason: unknown) => {
        if (!disposed) setError(errorMessage(reason));
      });
    return () => {
      disposed = true;
      if (instanceIdRef.current === instanceId) {
        createdRef.current = false;
        syncBoundsRef.current = null;
        if (pageRef) pageRef.current = null;
        webviewRef.current?.remove();
        webviewRef.current = null;
        cursorRef.current?.destroy();
        cursorRef.current = null;
      }
      window.cancelAnimationFrame(animationFrame);
      observer?.disconnect();
      if (sync) {
        window.removeEventListener('resize', sync);
        window.removeEventListener('scroll', sync, true);
        window.visualViewport?.removeEventListener('resize', sync);
        window.visualViewport?.removeEventListener('scroll', sync);
      }
      void electronInvoke('browser_close', { id, instanceId }).catch(() => undefined);
    };
  }, [pageRef, id, mode, profile, profileId, workspaceRoot]);

  useEffect(() => {
    if (!createdRef.current) return;
    void electronInvoke('browser_navigate', {
      id,
      instanceId: instanceIdRef.current,
      url,
    })
      .then(() => {
        if (!selectionEnabledRef.current) return;
        return electronInvoke('browser_set_selection_mode', {
          enabled: true,
          id,
          instanceId: instanceIdRef.current,
        });
      })
      .catch((reason: unknown) => {
        setError(errorMessage(reason));
      });
  }, [id, url]);

  useEffect(() => {
    if (reloadKey > 0)
      void electronInvoke('browser_reload', { id, instanceId: instanceIdRef.current }).catch(
        (reason: unknown) => {
          setError(errorMessage(reason));
        },
      );
  }, [id, reloadKey]);

  useEffect(() => {
    if (!createdRef.current) return;
    onSelectionModeErrorRef.current(null);
    void electronInvoke('browser_set_selection_mode', {
      enabled: selectionEnabled,
      id,
      instanceId: instanceIdRef.current,
    }).catch((reason: unknown) => {
      onSelectionModeErrorRef.current(errorMessage(reason));
    });
  }, [id, selectionEnabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(selected): the view syncs again when it becomes (or stops being) the browser's current tab, which syncBoundsRef reads through selectedRef.
  useEffect(() => {
    if (webviewRef.current)
      webviewRef.current.style.visibility = active || driven ? 'visible' : 'hidden';
    if (!createdRef.current) return;
    if (active || driven) syncBoundsRef.current?.();
    else
      void electronInvoke('browser_set_active', {
        active: false,
        id,
        instanceId: instanceIdRef.current,
      }).catch(() => undefined);
  }, [active, driven, id, selected]);

  // The activity event above shows the cursor in order with its moves; the hydrated `driven` state
  // covers an activity that began before this view listened.
  useEffect(() => {
    cursorRef.current?.setActive(driven);
  }, [driven]);

  return (
    <div className="relative min-h-0 flex-1 overflow-hidden bg-browser-canvas" ref={hostRef}>
      {error !== null && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-card p-6 text-center text-destructive text-sm">
          Erreur du navigateur : {error}
        </div>
      )}
    </div>
  );
}
